//@flow
/*
  ItemQty component, it renders a select component (selecting a value will
  trigger an action to update the quote) and an adjustable display qty
  component, when thre is no quote item associated with the sku (product never
  added to the cart) it will no render the select component and on click it will
  add the item to the cart (qty = 1).

  props:
    quoteItem: QuoteItemModel,
    sku: string,
    maxQty: Integer, max quantity allowed in the cart.
    displayComponentClass: Function, A react component class, an instance of
      this class will be generated, it will recieve qty and maxQty as props, if
      nothing is passed it will use a div element to display the quantity.
    className: a css class added to the result div.
    notAllowedReasons: Array, If this property is passed it will display an
      element with the reasons of why the product is not available on hover.

  note: there is always a select and a display element, if neccesary one of them
        can be hided with css.

  example: <ItemQty
              sku="test-sku"
              maxQty={20}
              displayComponentClass={ReactElementClass}
            />
*/

import React from 'react';
import QuoteActions from '../../Actions/quote';
import ModalActions from '../../Actions/modal';
import QuoteItemModel from '../../Stores/Quote/quoteItemModel';
import OutOfStockReminder from './OutOfStockReminder.react';
import Tooltip from 'rc-tooltip';
import CustomerModel from '../../Stores/Customer/customerModel';
import GiftModal from '../../Gifting/components/GiftModal.react';
import CookiesData from '../../Utils/cookies-data';
import GiftUtils from '../../Utils/gifts';
import Constants from '../../constants';

import type { List } from 'immutable';
import type CategoriesCollection from '../../Stores/MemberCategories/categoriesCollection';
import type QuoteItemsCollection from '../../Stores/Quote/quoteItemsCollection';
import type ProductsCollection from '../../Stores/Products/productsCollection';
import type ProductModel from '../../Stores/Products/productModel';
import type QuoteModel from '../../Stores/Quote/quoteModel';

type Props = {
  maxQty:number,
  minQty:number,
  className:string,
  displayComponentClass:?React$ComponentType<*>,
  quote?:?QuoteModel,
  quoteItem:?QuoteItemModel,
  quoteId:?string,
  sku:?string,
  inStock?:?boolean,
  preSale?:?boolean,
  customer?:CustomerModel,
  showOOSView?:'product'|'tooltip',
  stockReminders?:List<string>,
  inAlsoLike?:boolean,
  idx?:number,
  userCountry?:string,
  notAllowedReasons?:?string[],
  onAdd?:() => void,
  inCheckout?:boolean,
  quoteItems:QuoteItemsCollection,
  categories:CategoriesCollection,
  products:ProductsCollection,
};

type State = {
  readyToAdd:boolean,
  giftModalData:?{
    numItems:number,
    quoteId:string,
    product:ProductModel,
    productIsGift:boolean,
    giftBoxInQuote?:boolean,
  },
};

export default class ItemQty extends React.Component<Props, State> {
  enabled:boolean = true;

  static defaultProps = {
    maxQty: 20,
    minQty: 0,
    inStock: true,
  };

  state = {
    readyToAdd: false,
    giftModalData: undefined,
  }

  _disable() {
    this.enabled = false;
  }

  _enable() {
    this.enabled = true;
  }

  UNSAFE_componentWillUpdate(nextProps:Props, nextState:State) {
    this._enable();
  }

  _renderContent() {
    if (this.props.notAllowedReasons) return this._notAllowedRender();
    if (!this._getInStock()) return this._noStockItemRender();
    if (this.props.preSale) return this._preSaleItemRender();
    if (!this._getQuoteItem()) return this._noQuoteItemRender();
    return this._inCartItemRender();
  }

  render() {
    if (this.props.quoteItem && this.props.quoteItem.isVirtual()) return null;
    return (
      <div>
        {this._renderContent()}
        {this._renderGiftModal()}
      </div>
    );
  }

  _renderGiftModal() {
    if (this.state.giftModalData) {
      return (
        <GiftModal
          {...this.state.giftModalData}
          quoteItems={this.props.quoteItems}
          close={this._hideGiftModal}
        />
      );
    }
  }

  _hideGiftModal = () => {
    this.setState({ giftModalData: undefined });
  }

  _getOutOfStockComponent() {
    const sku = this.props.sku || '';
    return (
      <OutOfStockReminder
        customer={this.props.customer}
        sku={sku}
        showView={this.props.showOOSView}
        stockReminders={this.props.stockReminders}
        comingSoon={this.props.categories.productIsComingSoon(sku)}
      />
    );
  }

  _noQuoteItemRender() {
    return (
      <div className={this._getClassName()} onClick={this.addItem}>
        {this._getDisplayComponent()}
      </div>
    );
  }

  //use this & idx prop to determine which class to move the arrow appropriately
  _getArrowClass() {
    const idx = this.props.idx + 1;
    let arrowClass = '';
    if (idx % 2 === 0) arrowClass += 'second-child ';
    if (idx % 3 === 0) arrowClass += 'third-child ';
    if (idx % 4 === 0) arrowClass += 'fourth-child ';

    return arrowClass;
  }

  _noStockItemRender() {
    const displayComponent = this._getDisplayComponent();

    if (!this.props.showOOSView) return (
      <div className={this._getClassName()}>
        {displayComponent}
      </div>
    );

    return this._getTooltipComponent(this._getOutOfStockComponent());
  }

  _notAllowedRender() {
    const notAllowedReasons = this.props.notAllowedReasons;
    if (!notAllowedReasons) return null;

    if (notAllowedReasons.length === 1) {
      return this._getTooltipComponent(<div>{notAllowedReasons[0]}</div>);
    }

    const reasons = notAllowedReasons.map((reason, i) => {
      return <li key={i}>{reason}</li>;
    });

    return this._getTooltipComponent(<ul>{reasons}</ul>);
  }

  _preSaleItemRender() {
    const displayComponent = !this._getQuoteItem() ? this._noQuoteItemRender() :
      this._inCartItemRender();

    if (!this.props.showOOSView) return displayComponent;

    const tooltip = (
      <div className="presale-reminder">
        This item is launching soon! We have limited quantities available. Pre-order today to reserve yours and we’ll ship it as soon as it’s available.
      </div>
    );

    const alsoLike = this.props.inAlsoLike ? 'also-like' : '';

    return (
      <div className={this._getClassName()}>
        <Tooltip
          prefixCls={`${this._getArrowClass()} item-tooltip ${alsoLike} nb-tooltip`}
          placement="top"
          trigger={['hover', 'click']}
          mouseLeaveDelay={0.5}
          destroyTooltipOnHide={true}
          overlay={tooltip}
        >
          <div className="presale-qty-holder">
            {displayComponent}
          </div>
        </Tooltip>
      </div>
    );
  }

  _getTooltipComponent(overlayComponent:React$Node) {
    const displayComponent = this._getDisplayComponent();
    const alsoLike = this.props.inAlsoLike ? 'also-like' : '';

    return (
      <div className={this._getClassName()}>
        <Tooltip
          prefixCls={`${this._getArrowClass()} item-tooltip ${alsoLike} nb-tooltip`}
          placement="bottom"
          trigger={['hover', 'click']}
          mouseLeaveDelay={0.5}
          destroyTooltipOnHide={true}
          overlay={overlayComponent}
        >
          <div className="oos-item-qty-holder">
            {displayComponent}
          </div>
        </Tooltip>
      </div>
    );
  }

  _inCartItemRender() {
    return (
      <div className={this._getClassName()}>
        <select
          className="select-item-qty"
          title="Item Quantity"
          aria-label="Item quantity"
          value={this._getQty()}
          onChange={this.updateQty}
        >
          {this._getSelectOptions()}
        </select>
        {this._getDisplayComponent()}
      </div>
    );
  }

  updateQty = (event:SyntheticInputEvent<HTMLSelectElement>) => {
    const quoteItem = this.props.quoteItem;
    if (!quoteItem || !this.enabled) return;

    const qty = event.currentTarget.value;
    this._disable(); // Avoid double-click issues. Re-enable on willUpdate

    if (qty == "0") {
      QuoteActions.deleteQuoteItem(quoteItem);
      return;
    }

    return QuoteActions.updateQuoteItemQty(quoteItem, Number(qty));
  };

  _getGiftModalData() {
    if (this.props.quoteItems.getSkus().every(sku => sku === GiftUtils.EGIFT_SKU)) return;
    const sku = this._getSku();
    const giftCategory = this.props.categories.findByUrlKey('m-gifts');
    const product = sku && this.props.products.getProductBySku(sku);
    const isGiftProduct = giftCategory && product && product.isInCategory(giftCategory);
    const quoteProducts = this.props.products.getProductsBySkus(this.props.quoteItems.getSkus());
    const giftModalData:Object = {
      numItems: quoteProducts.length,
      quoteId: this._getQuoteId(),
      product: product,
      productIsGift: isGiftProduct,
    };

    if (CookiesData.get('giftNotification') || !giftCategory || !product) return;

    if (quoteProducts.length === 1 && quoteProducts[0].isInCategory(giftCategory)) {
      giftModalData.giftBoxInQuote = true;
      return giftModalData;
    } else if (isGiftProduct && quoteProducts.length) {
      return giftModalData;
    }
  }

  addItem = () => {
    if (!this.enabled) return;
    const giftModalData = this._getGiftModalData();
    if (giftModalData) return this.setState({ giftModalData });

    this._disable(); // Avoid double-click issues. Re-enable on willUpdate
    this.props.onAdd && this.props.onAdd();

    if (this._isActiveSSQuote() && !this._isPaymentAllowed()) {
      this._openPaymentModal();
      this._enable();
      return;
    }
    QuoteActions.addQuoteItem(this._getQuoteId(), this._getSku());
  };


  _openPaymentModal() {
    const product = !this.props.sku ? null :
      this.props.products.getProductBySku(this.props.sku);

    ModalActions.openModal({
      name: Constants.PAYMENT_METHOD_MODAL,
      data: {
        addSku: this.props.sku,
        notAllowedMethods: product ? product.getNotAllowedPayments() : [],
        showS2SNotice: true,
      },
    });
  }

  _getQty() {
    const quoteItem = this._getQuoteItem();
    return quoteItem ? quoteItem.getQty() : 0;
  }

  _getSku() {
    return this.props.sku;
  }

  _isActiveSSQuote() {
    return this.props.quote && this.props.quote.isActiveSSQuote();
  }

  _isPaymentAllowed() {
    if (!this.props.sku || !this.props.quote) return true;
    const sku:string =  this.props.sku;
    const quote:QuoteModel = this.props.quote;
    const product = this.props.products.getProductBySku(sku);

    if (!product) return true;

    const notAllowedPayments = product.getNotAllowedPayments();
    const currentPayment = quote.getPaymentMethod();
    return !notAllowedPayments.includes(currentPayment);
  }

  _getQuoteId() {
    return this.props.quoteId;
  }

  _getQuoteItem() {
    return this.props.quoteItem;
  }

  _getMaxQty() {
    return this.props.maxQty;
  }

  _getInStock() {
    const sku = this.props.sku || '';
    const comingSoon = this.props.categories.productIsComingSoon(sku);

    return this.props.inStock && !comingSoon;
  }

  _getClassName() {
    return this.props.className;
  }

  _getDisplayComponentClass() {
    return this.props.displayComponentClass;
  }

  _getDisplayComponent() {
    const _class = this._getDisplayComponentClass();
    if(!_class) return this._defaultDisplayComponent();

    const sku = this.props.sku || '';

    const displayComponent = React.createElement(_class, {
      qty: this._getQty(),
      maxQty: this._getMaxQty(),
      inStock: this._getInStock(),
      isAllowed: Boolean(!this.props.notAllowedReasons),
      preSale: this.props.preSale,
      comingSoon: this.props.categories.productIsComingSoon(sku),
      inCheckout: this.props.inCheckout,
    });

    return displayComponent;
  }

  _defaultDisplayComponent() {
    return (
      <div className="display-item-qty" title={this._getQty()}>{this._getQty()}</div>
    );
  }

  _getSelectOptions() {
    const options = [];
    for (let i = this.props.minQty; i <= this.props.maxQty; i++) {
      options.push(<option key={i} value={i}>{i}</option>);
    }

    return options;
  }
}
